import { useEffect, useState } from 'react';
import { fetchOrderShipments } from '../services/orderApi';
import { formatMoney, formatDate } from '../utils/formatters';

const OrderResult = ({ order, rawOrder }) => {
  const [shipments, setShipments] = useState([]);
  const [shipmentStatus, setShipmentStatus] = useState('');

  useEffect(() => {
    let cancelled = false;

    async function loadShipments() {
      if (!order?.entityId || !order?.server) {
        setShipments([]);
        setShipmentStatus('');
        return;
      }

      setShipmentStatus('Loading shipments...');

      try {
        const data = await fetchOrderShipments(order.entityId, order.server);
        if (cancelled) {
          return;
        }

        const nextShipments = Array.isArray(data?.shipments) ? data.shipments : [];
        setShipments(nextShipments);
        setShipmentStatus(nextShipments.length ? '' : 'No shipments found yet.');
      } catch (err) {
        if (cancelled) {
          return;
        }

        setShipments([]);
        setShipmentStatus(err.message || 'Order shipment lookup failed');
      }
    }

    loadShipments();

    return () => {
      cancelled = true;
    };
  }, [order?.entityId, order?.server]);

  if (!order) {
    return null;
  }

  const orderItems = Array.isArray(order.items) ? order.items : [];
  const shipmentEntriesByItemId = shipments.reduce((acc, shipment) => {
    const shipmentItems = Array.isArray(shipment.items) ? shipment.items : [];

    shipmentItems.forEach(item => {
      const key = String(item.orderItemId);
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push({
        shipmentId: shipment.id,
        qty: Number(item.qty) || 0,
      });
    });

    return acc;
  }, {});

  return (
    <section className="result">
      <h2>Order Details</h2>

      <div className="summary">
        <div><strong>Order #:</strong> {order.id}</div>
        <div><strong>Server:</strong> {String(order.server || '').toUpperCase()}</div>
        <div><strong>Status:</strong> {order.status}</div>
        <div><strong>Date:</strong> {formatDate(order.createdAt)}</div>
        <div><strong>Total:</strong> {formatMoney(order.total, order.currency)}</div>
      </div>

      <h3>Items</h3>
      <ul id="items">
        {orderItems.map(item => {
          const shippedEntries = item.itemId != null ? (shipmentEntriesByItemId[String(item.itemId)] || []) : [];
          const shippedQty = shippedEntries.reduce((sum, entry) => sum + entry.qty, 0) || Number(item.qtyShipped) || 0;
          const orderedQty = Number(item.qty) || 0;
          const lineStatus = shippedQty <= 0
            ? 'Not shipped'
            : shippedQty < orderedQty
              ? `Partially shipped (${shippedQty}/${orderedQty})`
              : 'Shipped';

          return (
            <li key={item.itemId || item.sku}>
              <strong>{item.name}</strong><br />
              <span>Qty: {item.qty}</span><br />
              <span>Price: {formatMoney(item.price, order.currency)}</span>
              <br />
              <span>Line Status: {lineStatus}</span>
            </li>
          );
        })}
      </ul>

      <h3>Shipping</h3>
      <div className="shipping-layout">
        <div className="shipping-address-card">
          {order.shippingAddress ? (
            <>
              <strong>{[order.shippingAddress.firstname, order.shippingAddress.lastname].filter(Boolean).join(' ') || 'Shipping Address'}</strong>
              {order.shippingAddress.company && <div>{order.shippingAddress.company}</div>}
              {Array.isArray(order.shippingAddress.street) && order.shippingAddress.street.map(line => (
                <div key={line}>{line}</div>
              ))}
              <div>
                {[
                  order.shippingAddress.city,
                  order.shippingAddress.region_code || order.shippingAddress.region,
                  order.shippingAddress.postcode,
                ].filter(Boolean).join(', ')}
              </div>
              {order.shippingAddress.country_id && <div>{order.shippingAddress.country_id}</div>}
              {order.shippingAddress.telephone && <div>{order.shippingAddress.telephone}</div>}
            </>
          ) : (
            <div>Shipping address not available.</div>
          )}
          {order.shippingDescription && (
            <div className="shipping-method">{order.shippingDescription}</div>
          )}
        </div>

        <div className="shipment-section">
          {shipmentStatus && (
            <div className="shipment-status">{shipmentStatus}</div>
          )}

          {shipments.map(shipment => (
            <article key={shipment.entityId} className="shipment-card">
              <div className="shipment-card-header">
                <strong>{shipment.id}</strong>
                <span>{formatDate(shipment.createdAt)}</span>
              </div>

              {Array.isArray(shipment.tracks) && shipment.tracks.length > 0 && (
                <div className="shipment-tracks">
                  {shipment.tracks.map(track => (
                    <span
                      key={track.entityId || track.number}
                      className="tracking-link"
                    >
                      {track.title || track.carrierCode || 'Tracking'}: {track.number}
                    </span>
                  ))}
                </div>
              )}

              {Array.isArray(shipment.items) && shipment.items.length > 0 && (
                <ul className="shipment-item-list">
                  {shipment.items.map(item => (
                    <li key={item.entityId || `${shipment.entityId}-${item.orderItemId}-${item.sku}`}>
                      <strong>{item.sku}</strong> {item.name} x {item.qty}
                    </li>
                  ))}
                </ul>
              )}
            </article>
          ))}
        </div>
      </div>

      <pre className="raw-json">{JSON.stringify(rawOrder, null, 2)}</pre>
    </section>
  );
};

export default OrderResult;
